import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import type { SeccionAyuda, SeccionId } from './help-content';

/**
 * Piezas de la pantalla de Ayuda (`/help`).
 *
 * No hay ningún componente nuevo del sistema de diseño: todo se arma con las primitivas que ya
 * existen (`Card`/`CardContent`, `Badge`, `Alert`, `Table`) y con las clases de los tokens. Lo que
 * vive acá es solo lo que comparten las ocho secciones: el acordeón, los pasos numerados, las notas
 * y los enlaces internos.
 *
 * `EnlaceInterno` copia el enlace relacionado que ya usa Integraciones (mismo lenguaje visual,
 * mismos tokens) en vez de inventar otro estilo de tarjeta-enlace: el archivo de esa pantalla no se
 * toca a propósito.
 */

export interface BloqueSeccionProps {
  seccion: SeccionAyuda;
  /** ¿Está desplegada? Solo una sección está abierta a la vez (acordeón). */
  abierta: boolean;
  onToggle: (id: SeccionId) => void;
  children: ReactNode;
}

/**
 * Una sección de la guía: tarjeta con cabecera que se despliega.
 *
 * El contenido se deja SIEMPRE en el DOM y se oculta con el atributo `hidden`: así el
 * `aria-controls` del botón apunta a algo real, los enlaces con ancla funcionan y el texto se puede
 * buscar con Ctrl+F o imprimir aunque la sección esté cerrada.
 */
export function BloqueSeccion({ seccion, abierta, onToggle, children }: BloqueSeccionProps) {
  const Icon = seccion.icon;
  const contenidoId = `${seccion.id}-contenido`;

  return (
    <section id={seccion.id} className="scroll-mt-24">
      <Card>
        <h2>
          <button
            type="button"
            aria-expanded={abierta}
            aria-controls={contenidoId}
            onClick={() => onToggle(seccion.id)}
            className={cn(
              'flex w-full items-start gap-3 rounded-card px-6 py-6 text-left transition-colors',
              'hover:bg-surface-muted/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
            )}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
              <Icon size={18} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-card-title font-semibold text-ink">{seccion.titulo}</span>
              <span className="mt-0.5 block text-caption text-ink-muted">{seccion.bajada}</span>
            </span>

            <ChevronDown
              size={18}
              aria-hidden="true"
              className={cn('mt-1 shrink-0 text-ink-faint transition-transform', abierta && 'rotate-180')}
            />
          </button>
        </h2>

        <CardContent id={contenidoId} hidden={!abierta} className="space-y-5 pt-0 pb-6">
          {children}
        </CardContent>
      </Card>
    </section>
  );
}

/** Subtítulo dentro de una sección (el título de la sección es el `h2` de la cabecera). */
export function SubTitulo({ children }: { children: ReactNode }) {
  return <h3 className="text-body font-semibold text-ink">{children}</h3>;
}

/** Pasos numerados (el número es decorativo: el orden lo da el `<ol>`). */
export function ListaPasos({ pasos }: { pasos: { titulo: string; detalle: string }[] }) {
  return (
    <ol className="space-y-4">
      {pasos.map((paso, indice) => (
        <li key={paso.titulo} className="flex gap-3">
          <span
            aria-hidden="true"
            className="tabular flex size-7 shrink-0 items-center justify-center rounded-pill bg-brand-50 text-caption font-semibold text-brand-700"
          >
            {indice + 1}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block text-body font-medium text-ink">{paso.titulo}</span>
            <span className="mt-1 block text-caption text-ink-muted">{paso.detalle}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Lista de notas y explicaciones sueltas. */
export function ListaNotas({ notas }: { notas: string[] }) {
  return (
    <ul className="space-y-2">
      {notas.map((nota) => (
        <li key={nota} className="flex gap-2.5 text-caption text-ink-muted">
          <span aria-hidden="true" className="mt-1.5 size-1.5 shrink-0 rounded-pill bg-brand-600" />
          <span className="min-w-0 flex-1">{nota}</span>
        </li>
      ))}
    </ul>
  );
}

/** Tarjeta-enlace con icono (misma construcción que el enlace relacionado de Integraciones). */
export function EnlaceInterno({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-panel border border-line bg-surface p-4 transition-colors hover:border-brand-200 hover:bg-brand-50/60"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
        {icon}
      </span>

      <span className="min-w-0">
        <span className="block text-body font-medium text-ink">{title}</span>
        <span className="mt-0.5 block text-caption text-ink-muted">{description}</span>
      </span>

      <ChevronRight size={16} aria-hidden="true" className="mt-1 ml-auto shrink-0 text-ink-faint" />
    </Link>
  );
}

/** Enlaces de cierre de una respuesta (a la pantalla donde se resuelve). */
export function EnlacesAyuda({ enlaces }: { enlaces: { label: string; to: string }[] }) {
  if (enlaces.length === 0) return null;

  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      {enlaces.map((enlace) => (
        <Link
          key={enlace.to}
          to={enlace.to}
          className="text-caption font-medium text-brand-700 hover:underline"
        >
          {`Ir a ${enlace.label} →`}
        </Link>
      ))}
    </p>
  );
}
